import { useState, useEffect } from 'react'
import { useKnowledgeStore } from '@/stores/knowledge'
import { useUIStore } from '@/stores/ui'
import { supabase } from '@/lib/supabase'
import { BulkOperations } from '@/components/knowledge/bulk-operations'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { LoadingSpinner } from '@/components/ui/loading'
import { MarkdownPreview } from '@/components/ui/markdown-preview'
import { ConfirmDeleteDialog } from '@/components/ui/confirm-delete-dialog'
import { StaggerContainer, StaggerItem } from '@/components/animated/stagger-container'
import { InteractiveCard } from '@/components/animated/interactive-card'
import { Search, Filter, Plus, Edit, Trash2, Eye, CheckCircle, XCircle, Clock } from 'lucide-react'

interface FAQListProps {
  onSelectFAQ?: (faq: any, faqs?: any[]) => void
  onEditFAQ?: (faq: any) => void
  onCreateNew?: () => void
  enableBulkOperations?: boolean
  filter?: {
    status?: string
    category?: string
    source?: string
    tag?: string
  }
}

export function FAQList({ onSelectFAQ, onEditFAQ, onCreateNew, enableBulkOperations, filter }: FAQListProps) {
  const { faqs, isLoading, loadFAQs, searchFAQs, deleteFAQ } = useKnowledgeStore()
  const { preferences } = useUIStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState(filter?.status || 'all')
  const [categoryFilter, setCategoryFilter] = useState(filter?.category || 'all')
  const [escalationFilter, setEscalationFilter] = useState('all')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [sourcesMap, setSourcesMap] = useState<Map<string, string>>(new Map())

  // Use store preference if not explicitly overridden
  const bulkOperationsEnabled = enableBulkOperations !== undefined ? enableBulkOperations : preferences.enableBulkOperations
  // Animations automatically enabled when bulk operations are disabled
  const animationsEnabled = !bulkOperationsEnabled

  useEffect(() => {
    loadFAQs()
    // Fetch sources map whenever we need source filtering or display
    if (filter?.tag || filter?.source) {
      fetchSourcesMap()
    }
  }, [filter?.tag, filter?.source])

  const fetchSourcesMap = async () => {
    try {
      const { data, error } = await supabase
        .from('import_sources')
        .select('uid, source_type')

      if (error) throw error

      const map = new Map<string, string>()
      data?.forEach(source => {
        map.set(source.uid, source.source_type)
      })
      setSourcesMap(map)
    } catch (error) {
      console.error('Error fetching sources map:', error)
    }
  }

  const handleSearch = () => {
    if (searchQuery.trim()) {
      searchFAQs(searchQuery)
    } else {
      loadFAQs()
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteFAQ(id)
      // Remove from selection if deleted
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id))
    } catch (error) {
      console.error('Error deleting FAQ:', error)
    }
  }

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    )
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'draft':
        return <Clock className="h-4 w-4 text-yellow-500" />
      case 'review':
        return <Eye className="h-4 w-4 text-blue-500" />
      case 'archived':
        return <XCircle className="h-4 w-4 text-gray-500" />
      default:
        return null
    }
  }

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-missionSolar-primaryTeal text-white border-missionSolar-primaryTeal'
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-300'
      case 'review':
        return 'bg-amber-100 text-amber-800 border-amber-300'
      case 'draft':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      case 'archived':
        return 'bg-gray-50 text-gray-400 border-gray-200'
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300'
    }
  }

  const filteredFAQs = faqs.filter(faq => {
    // Apply URL-based filters from parent
    if (filter?.tag && (!faq.tags || !faq.tags.includes(filter.tag))) return false

    // Source filtering: check if sourcesMap has loaded and filter matches
    if (filter?.source) {
      if (!faq.source_uid) return false  // No source UID means it doesn't match
      const sourceType = sourcesMap.get(faq.source_uid)
      if (sourceType !== filter.source) return false
    }

    // Apply local dropdown filters
    if (statusFilter !== 'all' && faq.status !== statusFilter) return false
    if (categoryFilter !== 'all' && faq.category !== categoryFilter) return false
    if (escalationFilter !== 'all' && faq.escalation_rule !== escalationFilter) return false
    return true
  })

  const categories = ['all', 'wallbox', 'pv_system', 'battery', 'app_access', 'general']
  const escalationTypes = ['all', 'self_solve', 'mission_solar', 'neoom']
  const statuses = ['all', 'draft', 'review', 'approved', 'active', 'archived']

  return (
    <div className="space-y-3">
      {/* Bulk Operations Bar */}
      {bulkOperationsEnabled && (
        <BulkOperations
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onRefresh={loadFAQs}
        />
      )}

      {/* Compact Header with Search and Filters */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-3 w-3" />
            <Input
              placeholder="FAQs durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-8 h-8 text-sm"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-8 text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map(status => (
                <SelectItem key={status} value={status}>
                  {status === 'all' ? 'Alle Status' : status.charAt(0).toUpperCase() + status.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[140px] h-8 text-sm">
              <SelectValue placeholder="Kategorie" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(category => (
                <SelectItem key={category} value={category}>
                  {category === 'all' ? 'Alle Kategorien' : category.replace('_', ' ').toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={escalationFilter} onValueChange={setEscalationFilter}>
            <SelectTrigger className="w-[140px] h-8 text-sm">
              <SelectValue placeholder="Eskalation" />
            </SelectTrigger>
            <SelectContent>
              {escalationTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {type === 'all'
                    ? 'Alle Typen'
                    : type === 'self_solve'
                      ? 'Self Solve'
                      : type === 'mission_solar'
                        ? 'Mission Solar'
                        : 'Neoom'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button onClick={handleSearch} variant="secondary" size="sm" className="h-8">
            <Search className="mr-1 h-3 w-3" />
            Suchen
          </Button>

          <Button onClick={onCreateNew} size="sm" className="h-8">
            <Plus className="mr-1 h-3 w-3" />
            Neue FAQ
          </Button>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>{filteredFAQs.length} FAQs</span>
          {(searchQuery || categoryFilter !== 'all' || escalationFilter !== 'all' || statusFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('')
                setCategoryFilter('all')
                setEscalationFilter('all')
                setStatusFilter('all')
                loadFAQs()
              }}
            >
              Filter zurücksetzen
            </Button>
          )}
        </div>
      </div>

      {/* FAQ List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : filteredFAQs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">Keine FAQs gefunden</p>
            <Button onClick={onCreateNew}>
              <Plus className="mr-2 h-4 w-4" />
              Erste FAQ erstellen
            </Button>
          </CardContent>
        </Card>
      ) : animationsEnabled ? (
        <StaggerContainer className="space-y-2">
          {filteredFAQs.map((faq, index) => (
            <StaggerItem key={faq.id}>
              <InteractiveCard>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {bulkOperationsEnabled && (
                        <Checkbox
                          checked={selectedIds.includes(faq.id)}
                          onCheckedChange={() => handleToggleSelect(faq.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div className="flex items-start justify-between flex-1">
                        <div className="flex-1 space-y-1" onClick={() => onSelectFAQ?.(faq, filteredFAQs)}>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(faq.status)}
                            <h3 className="font-semibold text-base line-clamp-1">
                              {faq.question}
                            </h3>
                          </div>

                          <MarkdownPreview
                            content={faq.answer}
                            maxLength={100}
                            className="line-clamp-1 text-sm text-muted-foreground"
                          />

                          <div className="flex items-center gap-2 pt-1">
                            <Badge className={getStatusBadgeClass(faq.status)}>
                              {faq.status}
                            </Badge>

                            {faq.category && (
                              <Badge variant="outline">
                                {faq.category.replace('_', ' ')}
                              </Badge>
                            )}

                            {faq.tags && faq.tags.length > 0 && (
                              <>
                                {faq.tags.slice(0, 3).map(tag => (
                                  <Badge key={tag} variant="outline" className="text-xs text-muted-foreground border-muted">
                                    {tag}
                                  </Badge>
                                ))}
                                {faq.tags.length > 3 && (
                                  <span className="text-xs text-muted-foreground">
                                    +{faq.tags.length - 3} mehr
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                            <span>{new Date(faq.updated_at).toLocaleDateString('de-DE')}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              onEditFAQ?.(faq)
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <ConfirmDeleteDialog
                            title="FAQ löschen?"
                            description={`Möchten Sie die FAQ "${faq.question}" wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
                            onConfirm={() => handleDelete(faq.id)}
                            trigger={
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </InteractiveCard>
            </StaggerItem>
          ))}
        </StaggerContainer>
      ) : (
        <div className="space-y-2">
          {filteredFAQs.map((faq, index) => (
            <Card key={faq.id} className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {bulkOperationsEnabled && (
                    <Checkbox
                      checked={selectedIds.includes(faq.id)}
                      onCheckedChange={() => handleToggleSelect(faq.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="flex items-start justify-between flex-1">
                    <div className="flex-1 space-y-1" onClick={() => onSelectFAQ?.(faq, filteredFAQs)}>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(faq.status)}
                        <h3 className="font-semibold text-base line-clamp-1">
                          {faq.question}
                        </h3>
                      </div>

                      <MarkdownPreview
                        content={faq.answer}
                        maxLength={100}
                        className="line-clamp-1 text-sm text-muted-foreground"
                      />

                      <div className="flex items-center gap-2 pt-1">
                        <Badge className={getStatusBadgeClass(faq.status)}>
                          {faq.status}
                        </Badge>

                        {faq.category && (
                          <Badge variant="outline">
                            {faq.category.replace('_', ' ')}
                          </Badge>
                        )}

                        {faq.tags && faq.tags.length > 0 && (
                          <>
                            {faq.tags.slice(0, 3).map(tag => (
                              <Badge key={tag} variant="outline" className="text-xs text-muted-foreground border-muted">
                                {tag}
                              </Badge>
                            ))}
                            {faq.tags.length > 3 && (
                              <span className="text-xs text-muted-foreground">
                                +{faq.tags.length - 3} mehr
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                        <span>{new Date(faq.updated_at).toLocaleDateString('de-DE')}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation()
                          onEditFAQ?.(faq)
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <ConfirmDeleteDialog
                        title="FAQ löschen?"
                        description={`Möchten Sie die FAQ "${faq.question}" wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
                        onConfirm={() => handleDelete(faq.id)}
                        trigger={
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        }
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}